import {
    useIsMobileViewport,
    useUnsafeEffect,
} from "@wags/tanstack-start/react";
import {
    ExpandFilledIcon,
    PauseFilledIcon,
    PlayFilledIcon,
    ShrinkFilledIcon,
    Volume2FilledIcon,
    VolumeSlashFilledIcon,
} from "@wags/ui/product-icons.tsx";
import { VideoWithFallback } from "@wags/ui/video-with-fallback.tsx";
import { ImageErrorFallback } from "@wags/ui/vercel-image.tsx";
import { useRef, useState } from "react";
import styles from "./listing-media-panel.module.css";

function playbackTime(seconds: number) {
    const wholeSeconds = Math.floor(seconds);
    return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`;
}

export function ListingInlineVideo({
    src,
    label,
    onEnded,
}: {
    src: string;
    label: string;
    onEnded: () => void;
}) {
    const mobile = useIsMobileViewport();
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [paused, setPaused] = useState(true);
    const [muted, setMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [fullscreen, setFullscreen] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(1);
    const inlineMobile = mobile && !fullscreen;
    useUnsafeEffect(() => {
        const container = containerRef.current;
        const updateFullscreen = () =>
            setFullscreen(document.fullscreenElement === container);
        container?.addEventListener("fullscreenchange", updateFullscreen);
        return () =>
            container?.removeEventListener(
                "fullscreenchange",
                updateFullscreen,
            );
    }, []);
    const buttonClass =
        "flex size-11 shrink-0 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-true-white";

    return (
        <div
            ref={containerRef}
            className={`absolute inset-0 ${inlineMobile && aspectRatio >= 1 ? styles.ambientVideo : ""}`}
        >
            <div
                className={`absolute inset-0 ${inlineMobile ? "bottom-[var(--radius-surface)] [container-type:size]" : ""}`}
            >
                <VideoWithFallback
                    ref={videoRef}
                    src={src}
                    aria-label={label}
                    className={
                        inlineMobile
                            ? styles.inlineVideo
                            : "absolute inset-0 h-full w-full object-contain"
                    }
                    style={
                        inlineMobile
                            ? {
                                  width: `min(100cqw, calc(100cqh * ${aspectRatio}))`,
                                  height: `min(100cqh, calc(100cqw / ${aspectRatio}))`,
                              }
                            : undefined
                    }
                    controls={!mobile}
                    autoPlay
                    playsInline
                    onEnded={onEnded}
                    onPlay={() => setPaused(false)}
                    onPause={() => setPaused(true)}
                    onVolumeChange={(event) =>
                        setMuted(event.currentTarget.muted)
                    }
                    onDurationChange={(event) => {
                        const seconds = event.currentTarget.duration;
                        setDuration(Number.isFinite(seconds) ? seconds : 0);
                    }}
                    onLoadedMetadata={(event) => {
                        setAspectRatio(
                            event.currentTarget.videoWidth /
                                event.currentTarget.videoHeight,
                        );
                    }}
                    onTimeUpdate={(event) =>
                        setCurrentTime(event.currentTarget.currentTime)
                    }
                    fallback={
                        <ImageErrorFallback
                            alt={label}
                            className="absolute inset-0 h-full w-full"
                        />
                    }
                    overlay={
                        mobile ? (
                            <>
                                {inlineMobile && aspectRatio < 1 && (
                                    <div
                                        aria-hidden="true"
                                        className={styles.portraitShade}
                                    />
                                )}
                                <button
                                    type="button"
                                    aria-label={
                                        controlsVisible
                                            ? "Hide video controls"
                                            : "Show video controls"
                                    }
                                    aria-expanded={controlsVisible}
                                    className="absolute inset-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-true-white"
                                    onClick={() =>
                                        setControlsVisible(!controlsVisible)
                                    }
                                />
                                {controlsVisible && (
                                    <div
                                        role="group"
                                        aria-label="Video playback controls"
                                        className="absolute inset-x-0 bottom-0 px-3 pt-8 pb-2 text-true-white"
                                    >
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                aria-label={
                                                    paused
                                                        ? "Resume video"
                                                        : "Pause video"
                                                }
                                                className={buttonClass}
                                                onClick={() => {
                                                    const video =
                                                        videoRef.current;
                                                    if (!video) return;
                                                    if (video.paused) {
                                                        void video
                                                            .play()
                                                            .catch(() =>
                                                                setPaused(true),
                                                            );
                                                    } else video.pause();
                                                }}
                                            >
                                                {paused ? (
                                                    <PlayFilledIcon className="size-5" />
                                                ) : (
                                                    <PauseFilledIcon className="size-5" />
                                                )}
                                            </button>
                                            <span className="flex-1 text-xs tabular-nums">
                                                {playbackTime(currentTime)} /{" "}
                                                {playbackTime(duration)}
                                            </span>
                                            <button
                                                type="button"
                                                aria-label={
                                                    muted
                                                        ? "Unmute video"
                                                        : "Mute video"
                                                }
                                                className={buttonClass}
                                                onClick={() => {
                                                    const video =
                                                        videoRef.current;
                                                    if (video)
                                                        video.muted =
                                                            !video.muted;
                                                }}
                                            >
                                                {muted ? (
                                                    <VolumeSlashFilledIcon className="size-5" />
                                                ) : (
                                                    <Volume2FilledIcon className="size-5" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                aria-label={
                                                    fullscreen
                                                        ? "Exit video fullscreen"
                                                        : "Open video fullscreen"
                                                }
                                                className={buttonClass}
                                                onClick={() => {
                                                    const video =
                                                        videoRef.current;
                                                    if (fullscreen) {
                                                        void document.exitFullscreen();
                                                    } else if (
                                                        video &&
                                                        "webkitEnterFullscreen" in
                                                            video &&
                                                        typeof video.webkitEnterFullscreen ===
                                                            "function"
                                                    ) {
                                                        video.webkitEnterFullscreen();
                                                    } else {
                                                        void containerRef.current
                                                            ?.requestFullscreen()
                                                            .catch(() =>
                                                                setControlsVisible(
                                                                    true,
                                                                ),
                                                            );
                                                    }
                                                }}
                                            >
                                                {fullscreen ? (
                                                    <ShrinkFilledIcon className="size-5" />
                                                ) : (
                                                    <ExpandFilledIcon className="size-5" />
                                                )}
                                            </button>
                                        </div>
                                        <input
                                            type="range"
                                            aria-label="Video playback position"
                                            aria-valuetext={`${playbackTime(currentTime)} of ${playbackTime(duration)}`}
                                            min={0}
                                            max={duration || 1}
                                            step="any"
                                            value={Math.min(
                                                currentTime,
                                                duration,
                                            )}
                                            disabled={duration === 0}
                                            className="block h-6 w-full cursor-pointer accent-true-white"
                                            onChange={(event) => {
                                                const video = videoRef.current;
                                                if (video) {
                                                    video.currentTime =
                                                        event.currentTarget.valueAsNumber;
                                                    setCurrentTime(
                                                        video.currentTime,
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        ) : null
                    }
                />
            </div>
        </div>
    );
}
